import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  ActionIconContainer,
  AddFormButton,
  FilterButton,
} from "../../../shared/components/TableElements";
import { postApi } from "../../../api/getUser";
import { formatNumber } from "../../../shared/helpers";

/**
 * Generic confirmation modal for add-on / plan changes.
 * lineItems: [{ name, detail?, amount }]
 */
const getPaymentMethodLabel = (paymentData, t) => {
  if (!paymentData?.type || !paymentData?.details) return null;
  const { type, billing_email, details } = paymentData;
  switch (type) {
    case "card":
      return `${details.brand || t("card")} ${t("ending_in_sm")} ${details.last4 || "****"}`;
    case "us_bank_account":
      return `${details.bank_name || t("bank_account")} ${t("ending_in_sm")} ${details.last4 || "****"}`;
    case "sepa_debit":
      return `SEPA ${t("ending_in_sm")} ${details.last4 || "****"}`;
    case "upi":
      return `UPI: ${details.vpa || t("upi_id_unavailable")}`;
    case "link":
      return `Link: ${details.email || billing_email || t("email_unavailable")}`;
    case "cashapp":
      return `Cash App: ${details.cashtag || t("cash_app_unavailable")}`;
    default:
      return t("unknown_payment_method");
  }
};

const ConfirmAddonModal = ({
  show,
  onHide,
  lineItems = [],
  grandTotalLabel,
  grandTotalAmount,
  billingNote,
  paymentData,
  error,
  onConfirm,
  loading,
  previewParams,
  isAddonPurchase = false,
}) => {
  const { t } = useTranslation("common");
  const paymentMethodLabel = getPaymentMethodLabel(paymentData, t);

  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!show || !previewParams) return;
    const fetchPreview = async () => {
      setPreviewData(null);
      setPreviewLoading(true);
      try {
        const res = await postApi(
          { querystring: "preview_subscription_plan", ...previewParams },
          "subscription"
        );
        if (res?.success) setPreviewData(res);
      } finally {
        setPreviewLoading(false);
      }
    };
    fetchPreview();
  }, [show]);

  return (
    <Modal show={show} onHide={onHide} centered size="md">
      <Modal.Header>
        <h4>{t("confirm_changes")}</h4>
        <div className="d-flex justify-content-end">
          <ActionIconContainer onClick={onHide}>
            <i className="fi fi-rr-cross-small" />
          </ActionIconContainer>
        </div>
      </Modal.Header>

      <Modal.Body>
        <ConfirmIntro>{t("confirm_changes_intro")}</ConfirmIntro>

        <BreakdownTable>
          {lineItems.map((item, idx) => (
            <BreakdownRow key={idx}>
              <BreakdownLeft>
                <BreakdownName>{item.name}</BreakdownName>
                {item.detail && (
                  <BreakdownDetail>{item.detail}</BreakdownDetail>
                )}
              </BreakdownLeft>
              <BreakdownAmount>{item.amount}</BreakdownAmount>
            </BreakdownRow>
          ))}

          {/* Grand total row — kept for reference, replaced by Due Today below */}
          {/* {grandTotalAmount && (
            <TotalRow>
              <TotalLabel>
                {grandTotalLabel || t("new_monthly_total")}
              </TotalLabel>
              <TotalAmount>{grandTotalAmount}</TotalAmount>
            </TotalRow>
          )} */}

          {previewLoading && (
            <TotalRow $column>
              <TotalRowTop>
                <SkeletonBlock $width="80px" $height="14px" />
                <SkeletonBlock $width="100px" $height="18px" />
              </TotalRowTop>
              <SkeletonBlock $width="100%" $height="12px" style={{ marginTop: 4 }} />
              <SkeletonBlock $width="70%" $height="12px" />
            </TotalRow>
          )}

          {!previewLoading && previewData && (
            <TotalRow $column>
              <TotalRowTop>
                <TotalLabel>{t("due_today")}</TotalLabel>
                <TotalAmount>
                  {`${previewData.currency} ${formatNumber(previewData.due_today, 2)}`}
                </TotalAmount>
              </TotalRowTop>
              <DueTodaySubtext>
                {isAddonPurchase
                  ? t("due_today_addon_note", {
                      date: previewData.next_renewal_date,
                    })
                  : previewData.is_credit
                  ? t("due_today_credit_note", {
                      currency: previewData.currency,
                      amount: formatNumber(previewData.credit_amount, 2),
                      date: previewData.next_renewal_date,
                    })
                  : t("due_today_proration_note", {
                      days: previewData.days_remaining,
                      date: previewData.next_renewal_date,
                    })}
              </DueTodaySubtext>
            </TotalRow>
          )}
        </BreakdownTable>

        {paymentMethodLabel && (
          <PaymentMethodRow>
            <PaymentMethodLabel>{t("payment_method")}</PaymentMethodLabel>
            <PaymentMethodValue>{paymentMethodLabel}</PaymentMethodValue>
          </PaymentMethodRow>
        )}
        {/* {billingNote && <BillingNote>{billingNote}</BillingNote>} */}
        {error && <ErrorText>{error}</ErrorText>}
      </Modal.Body>

      <Modal.Footer className="d-flex gap-2">
        <FilterButton onClick={onHide} disabled={loading}>
          {/* {`← ${t("back")}`} */}
          {t("back")}
        </FilterButton>
        <AddFormButton onClick={onConfirm} disabled={loading || previewLoading}>
          {loading ? t("saving") : previewData?.is_credit ? t("confirm") : t("confirm_pay")}
        </AddFormButton>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmAddonModal;

// ── Styled components ─────────────────────────────────────────────────────────

const ConfirmIntro = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 16px;
`;

const BreakdownTable = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
`;

const BreakdownRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #e5e7eb;
`;

const BreakdownLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BreakdownName = styled.span`
  font-size: 13px;
  font-weight: 400;
  //color: #111827;
`;

const BreakdownDetail = styled.span`
  font-size: 11px;
  color: #9ca3af;
`;

const BreakdownAmount = styled.span`
  font-size: 13px;
  font-weight: 600;
  //color: #374151;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: ${({ $column }) => ($column ? "flex-start" : "center")};
  flex-direction: ${({ $column }) => ($column ? "column" : "row")};
  padding: 12px 14px;
  background: #f9fafb;
  gap: ${({ $column }) => ($column ? "4px" : "0")};
`;

const TotalRowTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const TotalLabel = styled.span`
  font-size: 13px;
  font-weight: 400;
  //color: #374151;
`;

const TotalAmount = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #111827;
`;

const DueTodaySubtext = styled.p`
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.5;
  margin: 0;
`;

const shimmer = `
  @keyframes shimmer {
    0% { background-position: -400px 0; }
    100% { background-position: 400px 0; }
  }
`;

const SkeletonBlock = styled.div`
  ${shimmer}
  width: ${({ $width }) => $width || "100%"};
  height: ${({ $height }) => $height || "14px"};
  border-radius: 4px;
  background: linear-gradient(90deg, #e5e7eb 25%, #f3f4f6 50%, #e5e7eb 75%);
  background-size: 800px 100%;
  animation: shimmer 1.4s infinite linear;
`;

const PaymentMethodRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding: 8px 0;
`;

const PaymentMethodLabel = styled.span`
  font-size: 13px;
  color: #9ca3af;
`;

const PaymentMethodValue = styled.p`
  font-size: 14px;
  font-weight: 500;
`;

const BillingNote = styled.p`
  font-size: 12px;
  color: #9ca3af;
  line-height: 1.6;
  margin-top: 8px;
  margin-bottom: 0;
`;

const ErrorText = styled.div`
  font-size: 13px;
  color: #ef4444;
  margin-top: 8px;
`;
